import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Table, Empty, Tag, Input, Tooltip, Select } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faX } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateFormAction, updateMessageType } from "../../../redux/messageType/messagesTypeSlice";
import { usePostMessageTypeMutation, usePutMessageTypeMutation } from "../../../redux/messageType/messagesTypeAPI";
// COMPONENTS
import TableNoDataBox from "../../components/table/TableNoDataBox";
import MessageTypeTableActions from "./MessageTypeTableActions";
import MessageTypeHeader from "./MessageTypeHeader";
import GlobalAlert2 from "../../home/GlobalAlert2";
// AUTHORIZATION
import validatePermission from "../../../utils/validatePermissions";
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// INTERFACES
import { RootState } from "../../../app/store";
import { MessageTypeInterface } from "../../../interfaces/MessageType.interface";
// CUSTOM HOOKS
import useGetAllMessagesTypes from "../../../customHooks/GET_requests/useGetAllMessagesTypes";
// CONSTANTS
import { BLANK_MESSAGE_TYPE } from "../../../constants/messagesTypes";

function MessageTypeTable() {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");
	const { Column, ColumnGroup } = Table;
	const { Option } = Select;
	const COLORS = [
		"gray",
		"magenta",
		"red",
		"volcano",
		"orange",
		"gold",
		"lime",
		"green",
		"cyan",
		"blue",
		"geekblue",
		"purple",
	];

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [messageType, setMessageType] = useState<MessageTypeInterface>({ ...BLANK_MESSAGE_TYPE });
	const [pagination, setPagination] = useState<any>({
		current: 1,
		pageSize: 10,
		showSizeChanger: true,
		pageSizeOptions: ["10", "20", "50", "100"],
	});

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [
		triggerPostMessageType,
		{
			isSuccess: isSuccessPostMessageType,
			isLoading: isLoadingPostMessageType,
			isError: isErrorPostMessageType,
			error: errorPostMessageType,
			reset: resetPostMessageType,
		},
	] = usePostMessageTypeMutation();

	const [
		triggerPutMessageType,
		{
			isSuccess: isSuccessPutMessageType,
			isLoading: isLoadingPutMessageType,
			isError: isErrorPutMessageType,
			error: errorPutMessageType,
			reset: resetPutMessageType,
		},
	] = usePutMessageTypeMutation();

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { theme } = useSelector((state: RootState) => state.home);
	const { permissions } = useSelector((state: RootState) => state.user);
	const { formAction, messageType: selectedMessageType } = useSelector((state: RootState) => state.messageType);

	// ************************************************ */
	// CUSTOM HOOKS *********************************** */
	const {
		messagesTypes,
		isLoadingGetAllMessagesTypes,
		errorMessageGetAllMessagesTypes,
		isErrorGetAllMessagesTypes,
		totalRowsGetAllMessagesTypes,
	} = useGetAllMessagesTypes({ page: pagination.current, limit: pagination.pageSize });

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const onChangeFormValue = (newValue: string, variableName: string) => {
		const copy: MessageTypeInterface = structuredClone(messageType);
		if (variableName === "name" && typeof newValue === "string") {
			copy.name = newValue;
		} else if (variableName === "color" && typeof newValue === "string") {
			copy.color = newValue;
		}
		setMessageType(copy);
	};

	const onClickSave = async () => {
		const token = await GETJwtToken();
		if (formAction === "create") {
			const BODY = {
				token,
				body: { ...messageType, id: totalRowsGetAllMessagesTypes + 1 },
			};
			triggerPostMessageType(BODY);
		} else {
			const BODY = {
				token,
				id: messageType.id,
				body: messageType,
			};
			triggerPutMessageType(BODY);
		}
	};

	const resetForm = () => {
		resetPostMessageType();
		resetPutMessageType();
		setMessageType(BLANK_MESSAGE_TYPE);
		dispatch(updateFormAction("create"));
		dispatch(updateMessageType(BLANK_MESSAGE_TYPE));
	};

	const checkFormIsValid = () => {
		let formIsValid = true;
		if (messageType.name === "") {
			formIsValid = false;
		}
		return formIsValid;
	};

	const handleTableChange = async (pagination_: any) => {
		setPagination({
			...pagination,
			current: pagination_.current,
			pageSize: pagination_.pageSize,
			total: pagination_.total,
		});
	};

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		resetForm();
	}, [isSuccessPostMessageType, isSuccessPutMessageType, isErrorPostMessageType, isErrorPutMessageType]);

	useEffect(() => {
		if (formAction === "create") {
			setMessageType(BLANK_MESSAGE_TYPE);
		}
	}, [formAction]);

	useEffect(() => {
		setMessageType(selectedMessageType);
	}, [selectedMessageType]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div>
			{/** ----------------------------------------------------- */}
			{/** ACTION FORM */}
			<div>
				{validatePermission("permission-not-defined", permissions) ? (
					<>
						<MessageTypeHeader />
						<Table
							locale={{
								emptyText: (
									<TableNoDataBox
										isError={isErrorGetAllMessagesTypes}
										errorMessage={errorMessageGetAllMessagesTypes}
										noItemsFound='No actions found'
									/>
								),
							}}
							rowClassName={(record, index) => getTableRowClass(index, theme)}
							dataSource={messagesTypes}
							pagination={{ ...pagination, total: totalRowsGetAllMessagesTypes }}
							onChange={handleTableChange}
							size='small'
							className='mt-3'
							loading={isLoadingGetAllMessagesTypes || isLoadingPostMessageType || isLoadingPutMessageType}
							footer={() => (
								<div className='generalStyles__flexEnd'>
									<Tag>{totalRowsGetAllMessagesTypes} Rows</Tag>
								</div>
							)}
						>
							{/** NAME */}
							<ColumnGroup title='Name'>
								<Column
									title={
										<Input
											size='small'
											placeholder='Name'
											className='generalStyles__width100'
											value={formAction === "create" ? messageType.name : ""}
											disabled={formAction === "edit"}
											onChange={(event: React.FormEvent<HTMLInputElement>) => {
												onChangeFormValue(event.currentTarget.value, "name");
											}}
										/>
									}
									dataIndex='name'
									key='name'
									render={(text, record: MessageTypeInterface) => (
										<div>
											{formAction === "edit" && record.id === messageType.id ? (
												<Input
													size='small'
													className='generalStyles__width100'
													placeholder='Name'
													value={messageType.name}
													onChange={(event: React.FormEvent<HTMLInputElement>) => {
														onChangeFormValue(event.currentTarget.value, "name");
													}}
												/>
											) : (
												<span>{text}</span>
											)}
										</div>
									)}
								/>
							</ColumnGroup>
							{/** COLORS */}
							<ColumnGroup title='Message Sub Type'>
								<Column
									title={
										<Select
											className='generalStyles__width100'
											onChange={(value) => {
												onChangeFormValue(value, "color");
											}}
											disabled={formAction === "edit"}
											showSearch
											size='small'
											value={messageType.color}
											optionFilterProp='children'
										>
											{COLORS.map((item: string) => (
												<Option key={item} value={item}>
													<Tag className='generalStyles__width100px generalStyles__textCenter' color={item}>
														{item}
													</Tag>
												</Option>
											))}
										</Select>
									}
									dataIndex='color'
									key='color'
									render={(text: string, record: MessageTypeInterface) => (
										<div>
											{formAction === "edit" && record.id === messageType.id ? (
												<Select
													className='generalStyles__width100'
													onChange={(value) => {
														onChangeFormValue(value, "color");
													}}
													showSearch
													size='small'
													value={messageType.color}
													optionFilterProp='children'
												>
													{COLORS.map((item: string) => (
														<Option key={item} value={item}>
															<Tag className='generalStyles__width100px generalStyles__textCenter' color={item}>
																{item}
															</Tag>
														</Option>
													))}
												</Select>
											) : (
												<Tag className='generalStyles__width100px generalStyles__textCenter' color={text}>
													{text}
												</Tag>
											)}
										</div>
									)}
								/>
							</ColumnGroup>
							{/** ACTIONS */}
							<ColumnGroup align='center' title={t("edgeManagement.banks.actions")} width={200}>
								<Column
									align='center'
									width={200}
									title={
										<div className='generalStyles__flexCenter'>
											<div>
												<Tooltip title={t("general.save")}>
													<Button
														className={formAction === "create" && checkFormIsValid() ? "buttonStyle__10" : ""}
														disabled={!checkFormIsValid() || formAction === "edit"}
														onClick={onClickSave}
														icon={<FontAwesomeIcon icon={faSave} />}
													/>
												</Tooltip>
											</div>
											<div className='generalStyles__mlFix'>
												<Tooltip title={t("general.clear")}>
													<Button
														className={formAction === "create" ? "buttonStyle__14" : ""}
														onClick={resetForm}
														disabled={formAction === "edit"}
														icon={<FontAwesomeIcon icon={faX} />}
													/>
												</Tooltip>
											</div>
										</div>
									}
									key='action'
									render={(_: any, record: MessageTypeInterface) => (
										<div>
											{formAction === "edit" && record.id === messageType.id ? (
												<div className='generalStyles__flexCenter'>
													<div>
														<Tooltip title={t("general.save")}>
															<Button
																className='buttonStyle__10'
																disabled={!checkFormIsValid()}
																onClick={onClickSave}
																icon={<FontAwesomeIcon icon={faSave} />}
															/>
														</Tooltip>
													</div>
													<div className='generalStyles__mlFix'>
														<Tooltip title={t("general.cancel")}>
															<Button
																className='buttonStyle__14'
																onClick={resetForm}
																icon={<FontAwesomeIcon icon={faX} />}
															/>
														</Tooltip>
													</div>
												</div>
											) : (
												<MessageTypeTableActions selectedItem={record} />
											)}
										</div>
									)}
								/>
							</ColumnGroup>
						</Table>
					</>
				) : (
					<>
						<div className='generalStyles__noAccessToListTable'>
							<Empty
								description={t("organizationManagement.listNotAvailableOrganizations")}
								image={Empty.PRESENTED_IMAGE_SIMPLE}
							/>
						</div>
					</>
				)}
			</div>
			{/** ---------------------------------------------------- */}
			{/** GLOBAL ALERT */}
			<GlobalAlert2
				isError={isErrorPostMessageType}
				isSuccess={isSuccessPostMessageType}
				requestType='POST'
				error={errorPostMessageType}
				name='Message Type'
			/>
			<GlobalAlert2
				isError={isErrorPutMessageType}
				isSuccess={isSuccessPutMessageType}
				requestType='PUT'
				error={errorPutMessageType}
				name='Message Type'
			/>
		</div>
	);
}

export default MessageTypeTable;
